import React, { memo } from "react";
import { Box, Chip, Paper, Stack, Tooltip, Typography } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AudioMessage from "../../components/MessageHistory/AudioMessage";
import ImageMessage from "../../components/MessageHistory/ImageMessage";
import DocumentMessage from "../../components/MessageHistory/DocumentMessage";
import VideoMessage from "../../components/MessageHistory/VideoMessage";
import { formatPortalMessageDateTime } from "./clientPortalTicketListUtils";
import ClientPortalLazyMedia from "./ClientPortalLazyMedia";
import TeamChatbotInteractive from "../Team/components/TeamChatbotInteractive";
import { getMessageViaChannelLabel } from "../../utils/messageInteractionChannelLabel";
import { CHANNEL_TYPES } from "../../constants/channelCatalog";

function textCaption(msg) {
  const text = String(msg.text || "").trim();
  if (!text) return null;
  if (text === "[mensagem]") return null;
  if (/^\s*<a\s/i.test(text)) return null;
  if (msg.type === "audio") return null;
  return text;
}

function resolveBubbleChannelLabel(msg, isClient) {
  const originTipo = Number(msg.originTipo ?? msg.messageChannelTipo);
  if (originTipo === CHANNEL_TYPES.XPRO_CLIENTE) {
    return "Portal do cliente";
  }

  const enriched = { ...msg, tipo: msg.messageTipo ?? msg.tipo };
  const fromApi = String(msg.channelLabel || "").trim();
  if (fromApi && !/^canal$/i.test(fromApi)) return fromApi;
  const resolved = getMessageViaChannelLabel(enriched);
  if (resolved) return resolved;
  const name = String(msg.channelName || "").trim();
  if (name && !/^canal$/i.test(name)) return name;
  return "";
}

function shouldShowChannelLabel(msg, isClient) {
  const label = resolveBubbleChannelLabel(msg, isClient);
  if (!label) return false;
  if (isClient && /^(portal cliente|portal do cliente)$/i.test(label)) return false;
  return true;
}

function mediaPlaceholderHeight(type) {
  if (type === "audio") return 48;
  if (type === "video") return 160;
  if (type === "image") return 120;
  return 56;
}

function ClientPortalMessageBubble({ msg, onQuickReply, isSending = false, canReply = true }) {
  const isClient = msg.sender === "client";
  const caption = textCaption(msg);
  const formattedDate = formatPortalMessageDateTime(msg.createdAt);
  const channelDisplayLabel = resolveBubbleChannelLabel(msg, isClient);
  const showChannel = shouldShowChannelLabel(msg, isClient);
  const isOptimistic = Boolean(msg._optimistic);
  const showInteractive =
    canReply && !isClient && msg.chatbotMetadata && !isOptimistic;

  const bubble = (
    <Paper
      elevation={0}
      sx={{
        px: 1.25,
        py: 1,
        width: "fit-content",
        maxWidth: "100%",
        borderRadius: isClient ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
        bgcolor: isClient ? "primary.main" : "background.paper",
        color: isClient ? "primary.contrastText" : "text.primary",
        border: isClient ? "none" : "1px solid",
        borderColor: "divider",
      }}
    >
      {msg.type === "image" && msg.image ? (
        <Box sx={{ mb: caption ? 0.75 : 0 }}>
          {isOptimistic ? (
            <ImageMessage message={msg} />
          ) : (
            <ClientPortalLazyMedia minHeight={mediaPlaceholderHeight("image")}>
              <ImageMessage message={msg} />
            </ClientPortalLazyMedia>
          )}
        </Box>
      ) : null}

      {msg.type === "video" && msg.video ? (
        <Box sx={{ mb: caption ? 0.75 : 0 }}>
          {isOptimistic ? (
            <VideoMessage message={{ ...msg, url: msg.video }} />
          ) : (
            <ClientPortalLazyMedia minHeight={mediaPlaceholderHeight("video")}>
              <VideoMessage message={{ ...msg, url: msg.video }} />
            </ClientPortalLazyMedia>
          )}
        </Box>
      ) : null}

      {msg.type === "audio" && msg.audio ? (
        <Box sx={{ mb: caption ? 0.75 : 0, color: "inherit", maxWidth: { xs: 260, sm: 320 } }}>
          {isOptimistic ? (
            <AudioMessage message={msg} />
          ) : (
            <ClientPortalLazyMedia minHeight={mediaPlaceholderHeight("audio")}>
              <AudioMessage message={msg} />
            </ClientPortalLazyMedia>
          )}
        </Box>
      ) : null}

      {(msg.type === "file" || msg.document?.url) && msg.document?.url ? (
        <Box sx={{ mb: caption ? 0.75 : 0 }}>
          <ClientPortalLazyMedia minHeight={mediaPlaceholderHeight("file")}>
            <DocumentMessage
              message={{
                url: msg.document.url,
                name: msg.document.name,
                document: msg.document,
              }}
            />
          </ClientPortalLazyMedia>
        </Box>
      ) : null}

      {caption ? (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {caption}
        </Typography>
      ) : null}

      {!caption && !msg.image && !msg.video && !msg.audio && !msg.document?.url ? (
        <Typography variant="body2" color="text.secondary">
          Mensagem
        </Typography>
      ) : null}

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 0.5, opacity: 0.8, gap: 0.5 }}
      >
        {formattedDate ? (
          <Typography variant="caption" sx={{ fontSize: 10.5, lineHeight: 1.2 }}>
            {formattedDate}
          </Typography>
        ) : null}
        {isClient && msg.sendStatus === "sending" ? (
          <Tooltip title="Enviando…" placement="top">
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", lineHeight: 0 }}>
              <ScheduleIcon sx={{ fontSize: 13, opacity: 0.95, color: "primary.contrastText" }} />
            </Box>
          </Tooltip>
        ) : null}
        {isClient && msg.sendStatus === "error" ? (
          <Tooltip title={msg.errorMessage || "Falha ao enviar"} placement="top">
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", lineHeight: 0 }}>
              <ErrorOutlineIcon sx={{ fontSize: 13, color: "#ffcdd2" }} />
            </Box>
          </Tooltip>
        ) : null}
        {showChannel ? (
          <Chip
            size="small"
            label={`via ${channelDisplayLabel}`}
            sx={{
              height: 18,
              fontSize: 9.5,
              bgcolor: isClient ? "rgba(255,255,255,0.2)" : "action.hover",
              color: "inherit",
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        ) : null}
      </Stack>
    </Paper>
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isClient ? "flex-end" : "flex-start",
        mb: 0.75,
        px: { xs: 1, sm: 2 },
        py: 0.25,
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: "90%", sm: "min(560px, 68%)" },
          width: "fit-content",
        }}
      >
        {bubble}
        {showInteractive ? (
          <TeamChatbotInteractive
            metadata={msg.chatbotMetadata}
            disabled={isSending || !onQuickReply}
            onSelect={(label) => onQuickReply?.(msg.id, label)}
            maxWidth="100%"
          />
        ) : null}
      </Box>
    </Box>
  );
}

function areMessagesEqual(prev, next) {
  const a = prev.msg;
  const b = next.msg;
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id &&
    a.sendStatus === b.sendStatus &&
    a.errorMessage === b.errorMessage &&
    a.text === b.text &&
    a.type === b.type &&
    a.image === b.image &&
    a.video === b.video &&
    a.audio === b.audio &&
    a.document?.url === b.document?.url &&
    a.createdAt === b.createdAt &&
    JSON.stringify(a.chatbotMetadata) === JSON.stringify(b.chatbotMetadata)
  );
}

export default memo(ClientPortalMessageBubble, areMessagesEqual);
